import { useEffect, useRef, useState } from 'react';

export const SELF_HEAL_CAP_MS = 4000;

/**
 * KAN-867 — when a company is locked but its subscription is healthy, ask Stripe once (the same
 * `billing-refresh` the checkout return uses), then re-read billing status + company access so the
 * gate lifts live if the server now says full. ONCE per mount; every step non-fatal; capped at 4s.
 * @returns {{ checking: boolean }}
 */
export default function useLockedBillingSelfHeal({ active, reload, reloadAccess }) {
  const [done, setDone] = useState(false);
  const ran = useRef(false);
  const alive = useRef(true);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const accessRef = useRef(reloadAccess);
  accessRef.current = reloadAccess;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!active || ran.current) return;
    ran.current = true;
    const cap = setTimeout(() => { if (alive.current) setDone(true); }, SELF_HEAL_CAP_MS);
    (async () => {
      // Loaded on demand: only a locked-but-healthy company ever needs it (keeps the Stripe client out of the card's chunk).
      try { const { refreshBilling } = await import('../../services/billing/billingApi'); await refreshBilling(); } catch (_e) { /* non-fatal */ }
      try { await reloadRef.current?.(); } catch (_e) { /* non-fatal */ }
      try { await accessRef.current?.(); } catch (_e) { /* non-fatal */ }
      clearTimeout(cap);
      if (alive.current) setDone(true);
    })();
  }, [active]);

  // Derived in render (not set in an effect) so there is no painted frame between "active" and "checking".
  return { checking: active && !done };
}
